// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// Angular Material
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

// Third party
import { takeUntil } from 'rxjs';

// Local library
import {
  AccountFacade,
  WageringLimitsService
} from '@amwager/account/data-access';
import { SessionService } from '@amwager/data-access';
import {
  DateFormatPipe,
  DecimalInputDirective,
  MatNotificationService,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';

// Debounce before a limit change is persisted (mirrors mobi)
const SAVE_DEBOUNCE_MS = 500;

/**
 * Wagering limits form (bet limit + NY daily-loss limit)
 */
@Component({
  selector: 'feature-mat-wagering-limits',
  templateUrl: './wagering-limits.component.html',
  styleUrls: ['./wagering-limits.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatInputModule,
    MatFormFieldModule,
    DecimalInputDirective,
    DateFormatPipe,
    TranslatePipe
  ]
})
export class WageringLimitsComponent
  extends UnsubscribeObservableService
  implements OnInit {

  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  isWagerLimit = false;
  wagerLimit = 0;
  wagerLimitCopy = 0;
  activeBetLimit = false;

  wagerDailyLossLimit = 0;
  wagerDailyLossLimitCopy = 0;
  pendingLossLimit = 0;
  pendingLossLimitEffective = '';
  isTriggeredDailyLoss = false;

  isProcessing = false;
  currentState = '';

  constructor(
    private readonly accountFacade: AccountFacade,
    private readonly wageringLimitsService: WageringLimitsService,
    private readonly sessionService: SessionService,
    private readonly matNotificationService: MatNotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.currentState = this.sessionService.getState();

    this.accountFacade.getAccountPreferencesAPI();
    this.accountFacade.isGetAccountPreferencesSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined) {
          this.isProcessing = false;
          this.cdRef.markForCheck();
          return;
        }

        const response = callback?.response?.AccountPreferences;

        this.isWagerLimit = response?.IsWagerLimit;
        this.wagerLimit = response?.WagerLimit;
        this.wagerLimitCopy = response?.WagerLimit;
        this.activeBetLimit = !this.isWagerLimit;
        this.isProcessing = false;

        this.cdRef.markForCheck();
      });

    if (this.currentState === 'NY')
      this.accountFacade.getAccountDailyLossLimitAPI();

    this.accountFacade.isGetAccountDailyLossLimitSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined || callback?.isSuccess === false) {
          if (callback?.isSuccess === false)
            this.showAlertMessage(callback?.response?.error?.Error);

          this.wagerDailyLossLimit = this.wagerDailyLossLimitCopy;
          this.isProcessing = false;
          this.cdRef.markForCheck();
          return;
        }

        const response = callback?.response;
        this.wagerDailyLossLimit = response?.DailyLossLimit || 0;
        this.wagerDailyLossLimitCopy = this.wagerDailyLossLimit;

        if (this.isTriggeredDailyLoss) {
          this.pendingLossLimit = response?.PendingLossLimit || 0;
          this.pendingLossLimitEffective = response?.PendingLossLimitEffective || '';
          setTimeout(() => {
            this.isTriggeredDailyLoss = false;
            this.cdRef.markForCheck();
          }, 5000);
        }

        this.isProcessing = false;
        this.cdRef.markForCheck();
      });
  }

  /**
   * Validate + debounce a bet-limit amount change
   * @param {Event} event Keyup event from the amount input
   * @returns {void}
   */
  onAmount(event: Event): void {
    const amount = (event.target as HTMLInputElement).value;

    const check = this.wageringLimitsService.validateWagerLimit(amount);
    if (!check.valid) {
      this.showAlertMessage(check.message ?? '');
      if (Number(amount) > 0) this.wagerLimit = this.wagerLimitCopy;
      return;
    }

    this.clearSaveTimer();
    this.saveTimer = setTimeout(() => this.onBetLimit(amount), SAVE_DEBOUNCE_MS);
  }

  /**
   * Persist the bet-limit amount
   * @param {string} amount Validated amount
   * @returns {void}
   */
  onBetLimit(amount: string): void {
    this.isProcessing = true;
    this.saveTimer = null;
    this.accountFacade.upsertAccountPrefenceFieldAPI('WagerLimit', amount);
  }

  /**
   * Toggle whether the bet limit is active
   * @returns {void}
   */
  toggleCheckbox(): void {
    this.isWagerLimit = !this.isWagerLimit;
    this.activeBetLimit = !this.isWagerLimit;
    this.isProcessing = true;

    this.accountFacade.upsertAccountPrefenceFieldAPI(
      'IsWagerLimit',
      this.isWagerLimit ? 'true' : 'false'
    );
  }

  /**
   * Validate + debounce a daily-loss-limit change (NY only)
   * @param {Event} event Keyup event from the amount input
   * @returns {void}
   */
  onDailyLossLimitAmount(event: Event): void {
    const amount = (event.target as HTMLInputElement).value;

    const check = this.wageringLimitsService.validateDailyLossLimit(amount);
    if (!check.valid) {
      this.showAlertMessage(check.message ?? '');
      this.clearSaveTimer();
      return;
    }

    this.clearSaveTimer();
    this.saveTimer = setTimeout(() => this.onDailyLossLimit(amount), SAVE_DEBOUNCE_MS);
  }

  /**
   * Persist the daily-loss-limit amount
   * @param {string} amount Validated amount
   * @returns {void}
   */
  onDailyLossLimit(amount: string): void {
    this.isProcessing = true;
    this.saveTimer = null;
    this.isTriggeredDailyLoss = true;
    this.accountFacade.updatetAccountDailyLossLimitAPI(Number(amount));
  }

  /**
   * Cancel any pending debounced save
   * @returns {void}
   */
  private clearSaveTimer(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }

  /**
   * Show a single-button info alert
   * @param {string} message Message to display
   * @returns {void}
   */
  private showAlertMessage(message: string): void {
    this.matNotificationService.showAlert('Info', message, '', '', 'OK');
  }
}
